import React, { useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { Badge, Box, Button, Flex, Heading, Table, TableContainer, Tbody, Td, Th, Thead, Tr } from '@chakra-ui/react';
import { getCounterPartyCustomerBalances } from 'api/internaltools';
import { PARTNER_VISIBILITY_ROUTE } from 'constants/routes';
import Pagination from 'modules/approve-transactions-v2/components/Pagination';
import LoadingErrorWrapper from 'components/LoadingErrorWrapper/LoadingErrorWrapper';

const DEFAULT_PAGE_SIZE = 10;

const formatNumber = (value) => {
  if (value === null || value === undefined) return '-';

  const num = Number(value);

  if (Number.isNaN(num)) return '-';

  return num.toLocaleString(undefined, { maximumFractionDigits: 8 });
};

export default function PartnerCustomerBalances() {
  const { partnerCode } = useParams();
  const history = useHistory();

  const [records, setRecords] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchData = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await getCounterPartyCustomerBalances(partnerCode, {
        page: currentPage,
        pageSize: DEFAULT_PAGE_SIZE,
      });
      const data = response?.data?.data ?? {};

      setRecords(data?.records ?? []);
      setTotalPages(data?.total_pages ?? 0);
      setTotalRecords(data?.total_records ?? 0);
    } catch (err) {
      setError(err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [partnerCode, currentPage]);

  const handlePreviousPage = () => {
    if (currentPage > 1) setCurrentPage(currentPage - 1);
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) setCurrentPage(currentPage + 1);
  };

  return (
    <Box pt='30px'>
      <Flex justify='space-between' align='center' mb={4}>
        <Box>
          <Heading as='h2' size='md' color='gray.700'>
            {partnerCode} Customer Balances
            {totalRecords > 0 && (
              <Badge colorScheme='gray' rounded='full' px={2.5} py={0.5} ml={2}>
                {totalRecords}
              </Badge>
            )}
          </Heading>
        </Box>
        <Button size='sm' onClick={() => history.push(PARTNER_VISIBILITY_ROUTE)}>
          Back to Partner Visibility
        </Button>
      </Flex>

      <LoadingErrorWrapper
        isLoading={isLoading}
        errorTitle={`Error fetching customer balances for ${partnerCode}.`}
        error={error}
      >
        <TableContainer borderRadius='lg' border='1px solid' borderColor='gray.200' overflowX='auto'>
          <Table variant='simple' size='md'>
            <Thead bg='gray.100'>
              <Tr>
                <Th>Customer Name</Th>
                <Th>Customer ID</Th>
                <Th>Currency</Th>
                <Th isNumeric>Available</Th>
                <Th isNumeric>Hold</Th>
                <Th isNumeric>Total</Th>
              </Tr>
            </Thead>
            <Tbody>
              {records.map((customer) =>
                customer?.balances?.length ? (
                  customer.balances.map((balance, idx) => (
                    <Tr key={`${customer.customer_id}-${balance.currency_code}-${idx}`}>
                      {idx === 0 ? (
                        <>
                          <Td rowSpan={customer.balances.length} fontWeight='bold' verticalAlign='top'>
                            {customer.customer_name || '-'}
                          </Td>
                          <Td rowSpan={customer.balances.length} verticalAlign='top'>
                            {customer.customer_id}
                          </Td>
                        </>
                      ) : null}
                      <Td>{balance.currency_code}</Td>
                      <Td isNumeric>{formatNumber(balance.available_balance)}</Td>
                      <Td isNumeric>{formatNumber(balance.hold_balance)}</Td>
                      <Td isNumeric fontWeight='bold'>
                        {formatNumber(balance.total_balance)}
                      </Td>
                    </Tr>
                  ))
                ) : (
                  <Tr key={customer.customer_id}>
                    <Td fontWeight='bold'>{customer.customer_name || '-'}</Td>
                    <Td>{customer.customer_id}</Td>
                    <Td colSpan={4} color='gray.500'>
                      No balances
                    </Td>
                  </Tr>
                )
              )}
            </Tbody>
          </Table>
        </TableContainer>

        {!records.length && !isLoading && (
          <Box textAlign='center' mt={4}>
            <Heading size='sm' color='gray.500'>
              No customers found for {partnerCode}
            </Heading>
          </Box>
        )}
      </LoadingErrorWrapper>

      {totalPages > 0 && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPreviousPage={handlePreviousPage}
          onNextPage={handleNextPage}
          isNextDisabled={currentPage >= totalPages}
        />
      )}
    </Box>
  );
}
